import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ExpandIndicatorComponent } from "src/app/common/components/expand.indicator/expand.indicator.component";
import { SharedModule } from "src/app/modules/shared/shared.module";
import {
  Itinerary,
  ItinerarySettings,
  MultiWidgetSettings,
} from "../../../models/itinerary";
import { ActivatedRoute, Router } from "@angular/router";
import { TripPlannerService } from "../../../services/trip.planner.service";
import { BaseComponent } from "src/app/common/components/base.component";
import { ButtonTriggerEvent } from "src/app/common/components/button/button.component";
import { finalize } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { getAllowedTextForUrl } from "src/app/common/utils/string.extensions";
import { ConfirmDialogComponent } from "src/app/common/components/confirm.dialog/confirm.dialog.component";
import { ConfigService } from "src/app/common/services/config/config.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "ig-itinerary-multi-widgets-manager",
  standalone: true,
  imports: [
    SharedModule,
    ExpandIndicatorComponent,
    ReactiveFormsModule,
    CommonModule,
    ConfirmDialogComponent,
  ],
  templateUrl: "./itinerary.multi.widgets.manager.component.html",
  styleUrl: "./itinerary.multi.widgets.manager.component.scss",
})
export class ItineraryMultiWidgetsManagerComponent extends BaseComponent {
  @Input() itineraries: Itinerary[] = [];
  @Input() _widgets: MultiWidgetSettings[] = [];
  @Input() set widgets(value: MultiWidgetSettings[]) {
    this._widgets = value;
    this.isShowDeleteConfirms = new Array(value.length).fill(false);
  }

  form: FormGroup;
  isAddingWidget = false;
  isShowDeleteConfirms = [];
  activeDeleteIndex: number | null = null;

  snackBar = inject(MatSnackBar);

  constructor(
    public activatedRoute: ActivatedRoute,
    private _tripService: TripPlannerService,
    private _router: Router,
    private _fb: FormBuilder
  ) {
    super({ activatedRoute });

    this.form = this._fb.group({
      title: new FormControl("", [
        Validators.required,
        Validators.maxLength(100),
        this.existingNameValidator.bind(this),
      ]),
    });
  }

  create(event: ButtonTriggerEvent) {
    if (!this.form.valid) {
      event.immediateComplete();
      return;
    }

    const newCode = getAllowedTextForUrl(
      this.form.value.title.toLowerCase()
    ).replaceAll(" ", "-");
    const newTitle = this.form.value.title.trim();

    if (newCode === "edit" || newTitle.toLowerCase() === "edit") {
      this.snackBar.open('Widget name cannot be "edit"', "Close", {
        horizontalPosition: "center",
        verticalPosition: "top",
        panelClass: "ig-error-snackbar",
        duration: 4000,
      });
      // event.immediateComplete();
      return;
    }
    if (!newCode) {
      this.snackBar.open("Please pick a new name for the widget.", "Close", {
        horizontalPosition: "center",
        verticalPosition: "top",
        panelClass: "ig-error-snackbar",
        duration: 4000,
      });
      // event.immediateComplete();
      return;
    }

    // Check for duplicates
    const duplicateWidget = this._widgets.some(
      (w) =>
        w.title.toLowerCase() === newTitle.toLowerCase() || w.code === newCode
    );

    if (duplicateWidget) {
      this.snackBar.open("Widget with this name already exists", "Close", {
        horizontalPosition: "center",
        verticalPosition: "top",
        panelClass: "ig-error-snackbar",
        duration: 4000,
      });
      event.immediateComplete();
      return;
    }

    this._widgets = [
      ...this._widgets,
      {
        title: this.form.value.title,
        code: newCode,
        settings: new ItinerarySettings(),
      },
    ];

    this.form.get("title")?.setValue("");
    this.isAddingWidget = false;

    this._tripService
      .updateMultiWidgetSettings(this.hubName, this._widgets)
      .pipe(finalize(event.complete))
      .subscribe(() => {
        this.snackBar.open(
          `All changes have been saved successfully.`,
          "Close",
          {
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: "ig-success-snackbar",
            duration: 4000,
          }
        );

        // setTimeout(() => {
        //   this._router.navigate([
        //     `/${this.hubName}/itineraries/${newCode}/edit`,
        //   ]);
        //   setTimeout(() => {
        //     window.location.reload();
        //   }, 200);
        // }, 500);
      });
  }

  async enterWidget(code: string) {
    // navigate to widget url
    await this._router.navigate([`${this.hubName}/itineraries/${code}/edit`]);
    // reload the page
    window.location.reload();
  }

  editWidget(widgetId: string) {
    // Implement edit logic
    console.log("Editing widget:", widgetId);
  }

  deleteWidgetConfirmed() {
    if (this.activeDeleteIndex === null) return;

    const widget = this._widgets[this.activeDeleteIndex];
    this.deleteWidget(widget.code, this.activeDeleteIndex);
    this.activeDeleteIndex = null;
  }

  deleteWidget(widgetCode: string, index: number) {
    // Create new filtered array instead of mutating
    this._widgets = this._widgets.filter((_, i) => i !== index);
    this._tripService
      .updateMultiWidgetSettings(this.hubName, this._widgets)
      .subscribe(() => {
        // set all items in this.isShowDeleteConfirms to false
        this.isShowDeleteConfirms = this.isShowDeleteConfirms.map(() => false);

        this.snackBar.open(
          `All changes have been saved successfully.`,
          "Close",
          {
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: "ig-success-snackbar",
            duration: 4000,
          }
        );

        setTimeout(() => {
          // Check if we're currently viewing the deleted widget
          const currentPath = this._router.url;
          if (currentPath.includes(`/itineraries/${widgetCode}/edit`)) {
            this._router.navigate([
              `/${this.hubName}/itineraries/default/edit`,
            ]);
          }
          setTimeout(() => {
            window.location.reload();
          }, 200);
        }, 500);
      });
  }

  getItineraryCount(widgetCode: string) {
    return this.itineraries?.filter((x) => x.widgetCodes.includes(widgetCode))
      .length;
  }

  isWidgetActive(widgetCode: string): boolean {
    if (
      widgetCode === "default" &&
      this._router.url.includes("/itineraries/edit")
    )
      return true;

    const processedCode = widgetCode.toLowerCase().replaceAll(" ", "-");
    return this._router.url.includes(`/itineraries/${processedCode}/`);
  }

  getActiveWidget(): MultiWidgetSettings | undefined {
    return this._widgets.find((w) => this.isWidgetActive(w.code));
  }

  getWidgetUrl(widgetCode: string): string {
    const url = `<div id="imgoingcalendar-wrapper" route="/${this.hubName}/itineraries/${widgetCode}/" url-change="true"> <app-root></app-root> <script type="text/javascript" src="https://imgoingcalendar.com/imgoingscript.js"></script> </div>`;
    // return `${environment.serverDomain}/${this.hubName}/itineraries/${widgetCode}/`;
    return url;
  }

  async copyWidgetLink(widgetCode: string) {
    const url = this.getWidgetUrl(widgetCode);
    try {
      await navigator.clipboard.writeText(url);
      this.snackBar.open("Link copied to clipboard", "Close", {
        horizontalPosition: "center",
        verticalPosition: "top",
        panelClass: "ig-success-snackbar",
        duration: 2000,
      });
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  }

  private existingNameValidator(
    control: FormControl
  ): { [key: string]: any } | null {
    const currentNames = this._widgets.map((w) => w.title.toLowerCase());
    const inputName = control.value?.trim().toLowerCase();
    return currentNames.includes(inputName) ? { nameExists: true } : null;
  }
}
